import { CampaignResponse } from '@/api/campaignPlaner/fetchCampaign';
import { DashboardRule } from '@/optimizer';
import moment from 'moment';

const isCampaignDateUpdated = (
    currentStartDate: string | undefined,
    currentEndDate: string | undefined,
    campaign: CampaignResponse,
): boolean =>
    !!(
        currentStartDate &&
        currentEndDate &&
        (currentStartDate !==
            moment.utc(campaign.startDate).format('DD-MM-YYYY') ||
            currentEndDate !==
                moment.utc(campaign.endDate).format('DD-MM-YYYY'))
    );

const getCampaignStateRules = (
    campaign: CampaignResponse,
    currentStartDate: string | undefined,
    currentEndDate: string | undefined,
) => {
    const campaignStateRules = campaign.campaignStateRules || [];

    return campaignStateRules.map(
        ({
            originalValue,
            isScalable,
            calculatedCampaignValue,
            isCampaignVariable,
            ...rule
        }) => {
            const isPercentageActiveDaysRule =
                rule.type === 'Set active days to' &&
                rule.subtype === '% of current days';

            const isBudgetRule =
                rule.type === 'Total budget' &&
                (rule.subtype === 'maximum' ||
                    rule.subtype === 'minimum' ||
                    rule.subtype === 'is exactly');

            if (isBudgetRule) {
                return {
                    ...rule,
                    value: originalValue,
                };
            }

            if (
                isScalable &&
                !isPercentageActiveDaysRule &&
                isCampaignDateUpdated(
                    currentStartDate,
                    currentEndDate,
                    campaign,
                )
            ) {
                const start = moment(currentStartDate, 'DD-MM-YYYY');
                const end = moment(currentEndDate, 'DD-MM-YYYY');

                const shouldScale = !isCampaignVariable;

                if (shouldScale) {
                    const duration = end.diff(start, 'days') + 1;
                    const scaledValue = Math.round(
                        Number(originalValue) * (duration / 366),
                    );
                    return {
                        ...rule,
                        value: scaledValue.toString(),
                    };
                } else {
                    return {
                        ...rule,
                        value: originalValue,
                    };
                }
            }

            return rule;
        },
    );
};

const buildDisabledChannelsRules = (campaign: CampaignResponse) => {
    const disabledChannelsRules: DashboardRule[] = (
        campaign.disabledChannels || []
    ).map((channel) => ({
        channel,
        type: 'Total budget',
        subtype: 'is exactly',
        value: '0',
        target: 'campaign', // Default target, though it might be overwritten later
    }));
    return disabledChannelsRules;
};

const isBudgetAmountRule = (rule: DashboardRule) =>
    rule.type === 'Total budget' &&
    (rule.subtype === 'minimum' ||
        rule.subtype === 'maximum' ||
        rule.subtype === 'is exactly');

// Back-office internal rules are entered as amounts for THIS campaign, but optimiser rules are
// yearly (the period context scales every channel limit by duration / 366), so the amount is
// converted the same way a locked channel's investment is in prepareRules.
const toYearlyInternalRules = (
    rules: DashboardRule[],
    currentStartDate: string | undefined,
    currentEndDate: string | undefined,
): DashboardRule[] => {
    if (!currentStartDate || !currentEndDate) {
        return rules;
    }
    const duration =
        moment(currentEndDate, 'DD-MM-YYYY').diff(
            moment(currentStartDate, 'DD-MM-YYYY'),
            'days',
        ) + 1;
    if (!(duration > 0)) {
        return rules;
    }
    return rules.map((rule) => {
        const amount = Number(rule.value);
        return isBudgetAmountRule(rule) && Number.isFinite(amount)
            ? { ...rule, value: Math.round(amount / (duration / 366)).toString() }
            : rule;
    });
};

export const getCampaignInternalRules = (
    campaign?: CampaignResponse,
    currentStartDate?: string,
    currentEndDate?: string,
): DashboardRule[] => {
    if (!campaign) {
        return [];
    }

    const internalRules = toYearlyInternalRules(
        campaign.internalRules || [],
        currentStartDate,
        currentEndDate,
    );
    const campaignStateRules = getCampaignStateRules(
        campaign,
        currentStartDate,
        currentEndDate,
    );
    const disabledChannelsRules = buildDisabledChannelsRules(campaign);

    // Rules should be deduplicated by channel, type, and subtype.
    // Priority: disabledChannelsRules > internalRules > campaignStateRules
    const rulesMap = new Map<string, DashboardRule>();

    const allRules = [
        ...campaignStateRules,
        ...internalRules,
        ...disabledChannelsRules,
    ];

    allRules.forEach((rule) => {
        const key = `${rule.channel}-${rule.type}-${rule.subtype}`;
        rulesMap.set(key, rule);
    });

    return Array.from(rulesMap.values());
};
